import { useGoogleLogin } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { useDispatch } from "react-redux";
import { useCallback } from 'react';
import { authService } from '../services/auth.service';
import Steps from '../enums/steps.enum';
import { AUTH_STATES } from '../constants/routes';
import { HttpService } from '../services/base.service';
import toast from 'react-hot-toast';
import { profileUpdate } from "../redux/slices/userSlice";


const timeZoneCityToCountry = { /* Your mapping here */ };

const useSocialLogin = (onLoginSuccess, setstate) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();


  const getCountry = async () => {
    try {
      if (Intl) {
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const tzArr = userTimeZone.split("/");
        const userCity = tzArr[tzArr.length - 1];
        return timeZoneCityToCountry[userCity] || "";
      }
    } catch (error) {
      console.error("Failed to get country", error);
      return "";
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (response) => {
      try {
        const country = await getCountry();

        const user = await authService.socialLogin({
          country,
          access_token: response.access_token,
          provider: "google",
          platform: "web",
        });

        const nextStep = user?.data?.nextStep;

        if (nextStep === Steps.ACCOUNT_LINKING) {
          Cookies.set("device_id", user?.data?.device_id, {
            domain: process.env.REACT_APP_PUBLIC_DOMAIN,
            path: "/",
            sameSite: "strict",
          });
          // Store email and provider in localStorage for LinkAccountModal
          localStorage.setItem("linkAccountEmail", user?.data?.data?.profile.email);
          localStorage.setItem("linkAccountProvider", "google");
          setstate(AUTH_STATES.LINK_ACCOUNT);
          return;
        } else if (nextStep === Steps.VERIFY_EMAIL) {
          Cookies.set("device_id", user?.data?.device_id, {
            domain: process.env.REACT_APP_PUBLIC_DOMAIN,
            path: "/",
            sameSite: "strict",
          });
          // Store email in localStorage for OTPVerifyModal
          localStorage.setItem("verifyEmail", user?.data?.data?.profile.email);
          setstate(AUTH_STATES.OTP_VERIFY);
          return;
        }

        if (user.status === 200) {
          Cookies.set("session_id", user?.data?.data?.access_token, {
            domain: process.env.REACT_APP_PUBLIC_DOMAIN,
            path: "/",
            sameSite: "strict",
          });

          Cookies.set("device_id", user?.data?.data?.device_id, {
            domain: process.env.REACT_APP_PUBLIC_DOMAIN,
            path: "/",
          });

          HttpService.setToken(user?.data?.data?.access_token);
          dispatch(profileUpdate(user.data));

          if (onLoginSuccess) onLoginSuccess();
        }
      } catch (error) {
        toast.error(t("Login Failed"));
        console.error("Login Failed", error);
      }
    },
    onError: (error) => {
      toast.error(t("Login Failed"));
      console.error("Login Failed", error);
    },
  });

  const handleFacebookLogin = async (response) => {
    try {
      const country = await getCountry();

      const user = await authService.socialLogin({
        country,
        access_token: response.accessToken,
        provider: "facebook",
        platform: "web",
      });

      const nextStep = user?.data?.nextStep;

      if (nextStep === Steps.ACCOUNT_LINKING) {
        Cookies.set("device_id", user?.data?.device_id, {
          domain: process.env.REACT_APP_PUBLIC_DOMAIN,
          path: "/",
          sameSite: "strict",
        });
        // Store email and provider in localStorage for LinkAccountModal
        localStorage.setItem("linkAccountEmail", user?.data?.data?.profile.email);
        localStorage.setItem("linkAccountProvider", "facebook");
        setstate(AUTH_STATES.LINK_ACCOUNT);
        return;
      }

      if (user.status === 200) {
        Cookies.set("session_id", user?.data?.data?.access_token, {
          domain: process.env.REACT_APP_PUBLIC_DOMAIN,
          path: "/",
          sameSite: "strict",
        });

        Cookies.set("device_id", user?.data?.data?.device_id, {
          domain: process.env.REACT_APP_PUBLIC_DOMAIN,
          path: "/",
        });

        HttpService.setToken(user?.data?.data?.access_token);

        if (onLoginSuccess) onLoginSuccess();
      }
    } catch (error) {
      toast.error(t("Login Failed"));
      console.error("Login Failed", error);
    }
  };

  const handleAppleLogin = useCallback(() => {
    setstate(AUTH_STATES.LINK_ACCOUNT);
    console.log("Apple login triggered");
  }, [setstate]);

  const handleAuthError = (error) => {
    if (error.response?.data?.status === 401) {
      toast.error(t("Session Expired"));
      localStorage.removeItem("session_id");
      Cookies.remove("session_id", {
        domain: process.env.REACT_APP_PUBLIC_DOMAIN,
        path: "/",
      });
      setstate(AUTH_STATES.LOGIN);
    } else {
      toast.error(error.response?.data?.message);
    }
  };

  return {
    handleGoogleLogin,
    handleFacebookLogin,
    handleAppleLogin,
  };
};

export default useSocialLogin; 